import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { CommunicationColors, Icon, IconButton, mergeStyleSets, Modal, NeutralColors, PrimaryButton, ScreenWidthMinLarge, ScreenWidthMinXLarge, Stack, Text } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { TelemetryImage } from '@shared/components/telemetryImage';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { checkoutBillingCountry } from '@shared/utils/checkoutUtils';
const modalStyles = {
    main: {
        [`@media (min-width: ${ScreenWidthMinXLarge}px)`]: {
            width: '30%'
        },
        [`@media (min-width: ${ScreenWidthMinLarge}px) and (max-width: ${ScreenWidthMinXLarge}px)`]: {
            width: '50%'
        },
        [`@media (max-width: ${ScreenWidthMinLarge}px)`]: {
            width: '80%'
        },
        display: 'flex'
    },
    scrollableContent: {
        padding: '16px 27px 24px 24px',
        [`@media (max-width: ${ScreenWidthMinLarge}px)`]: {
            padding: '16px 20px 24px 20px'
        }
    }
};
const consentModalClassNames = mergeStyleSets({
    appImg: {
        width: 44,
        height: 44,
        padding: 4,
        border: `1px solid ${NeutralColors.gray20}`
    },
    appImgContainer: {
        marginRight: 8,
        width: 44,
        height: 44
    },
    boldText: {
        fontWeight: 600
    },
    desclaimerContainer: {
        backgroundColor: NeutralColors.gray20,
        marginBottom: 20,
        marginTop: 12,
        '& a:link': {
            color: CommunicationColors.primary,
            textDecoration: 'underline'
        },
        '& a:visited': {
            color: CommunicationColors.primary
        },
        '& a:hover': {
            color: CommunicationColors.shade20
        },
        '& a:active': {
            color: CommunicationColors.shade30
        }
    }
});
const titleTokens = {
    padding: '0px 0px 20px 0px'
};
const loginUserInfoTokens = {
    padding: '20px 0px 4px'
};
const desclaimerTokens = {
    padding: 8
};
const infoIconStyles = {
    root: {
        fontSize: 16,
        marginRight: 8
    }
};
const iconButtonStyles = {
    root: {
        color: NeutralColors.gray160
    }
};
const cancelIcon = {
    iconName: 'Cancel'
};
export const BillingRegionMismatchModal = ({ context, dismissBillingRegionMismatchModal, appInfo: { iconURL, title, publisher } })=>{
    const [isModalOpen, { setFalse: hideModal }] = useBoolean(true);
    const { displayName, email } = useSelector(({ users })=>users);
    const { billingCountryCode } = useSelector(({ config })=>config);
    const { activeCustomer, customers } = useSelector(({ checkout })=>checkout);
    const userCheckoutBillingCountryCode = checkoutBillingCountry({
        activeCustomer,
        customers,
        defaultBillingCountry: billingCountryCode
    });
    const closeModal = ()=>{
        hideModal();
        dismissBillingRegionMismatchModal();
    };
    return /*#__PURE__*/ _jsx(Modal, {
        isOpen: isModalOpen,
        onDismiss: closeModal,
        styles: modalStyles,
        children: /*#__PURE__*/ _jsxs(Stack, {
            children: [
                /*#__PURE__*/ _jsx(Stack.Item, {
                    children: /*#__PURE__*/ _jsxs(Stack, {
                        horizontal: true,
                        tokens: titleTokens,
                        children: [
                            /*#__PURE__*/ _jsx(Stack.Item, {
                                grow: true,
                                children: /*#__PURE__*/ _jsx(Text, {
                                    variant: "xLarge",
                                    children: context.loc('BillingCountry_mismatch_title', 'Billing Country Mismatch.')
                                })
                            }),
                            /*#__PURE__*/ _jsx(Stack.Item, {
                                children: /*#__PURE__*/ _jsx(IconButton, {
                                    styles: iconButtonStyles,
                                    iconProps: cancelIcon,
                                    ariaLabel: context.loc('Dialog_Close', 'Close'),
                                    onClick: closeModal
                                })
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Stack.Item, {
                    children: /*#__PURE__*/ _jsxs(Stack, {
                        horizontal: true,
                        children: [
                            /*#__PURE__*/ _jsx(Stack.Item, {
                                align: "center",
                                children: /*#__PURE__*/ _jsx(TelemetryImage, {
                                    imgClassName: consentModalClassNames.appImg,
                                    className: classNames(consentModalClassNames.appImgContainer, {
                                        'ms-depth-8': true
                                    }),
                                    src: iconURL,
                                    "aria-label": context.loc('Telemetry_Image_Aria_Label', 'Application Icon'),
                                    alt: title
                                })
                            }),
                            /*#__PURE__*/ _jsxs(Stack, {
                                children: [
                                    /*#__PURE__*/ _jsx(Stack.Item, {
                                        children: /*#__PURE__*/ _jsx(Text, {
                                            variant: "mediumPlus",
                                            className: consentModalClassNames.boldText,
                                            children: title
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Stack.Item, {
                                        children: /*#__PURE__*/ _jsx(Text, {
                                            variant: "medium",
                                            children: context.locParams('Tile_By', [
                                                publisher
                                            ])
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Stack.Item, {
                    children: /*#__PURE__*/ _jsx(Stack, {
                        tokens: loginUserInfoTokens,
                        children: /*#__PURE__*/ _jsx(Stack.Item, {
                            children: /*#__PURE__*/ _jsxs(Text, {
                                variant: "medium",
                                children: [
                                    context.loc('Leads_Logged_User_Info', "You're signed in as"),
                                    ' ',
                                    /*#__PURE__*/ _jsxs(Text, {
                                        variant: "medium",
                                        className: consentModalClassNames.boldText,
                                        children: [
                                            displayName,
                                            " (",
                                            email,
                                            ")."
                                        ]
                                    })
                                ]
                            })
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(Stack.Item, {
                    children: /*#__PURE__*/ _jsxs(Stack, {
                        horizontal: true,
                        tokens: desclaimerTokens,
                        className: consentModalClassNames.desclaimerContainer,
                        children: [
                            /*#__PURE__*/ _jsx(Icon, {
                                iconName: "info",
                                styles: infoIconStyles
                            }),
                            context.locParams('BillingCountry_mismatch_message', [
                                billingCountryCode.toUpperCase(),
                                userCheckoutBillingCountryCode.toUpperCase()
                            ], "Your selected billing country ({0}) doesn't match the country of your saved payment method ({1}). To continue please update the billing country using the globe icon near your profile.")
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Stack.Item, {
                    align: "end",
                    children: /*#__PURE__*/ _jsxs(PrimaryButton, {
                        onClick: closeModal,
                        children: [
                            " ",
                            context.loc('CTA_Ok', 'Ok'),
                            " "
                        ]
                    })
                })
            ]
        })
    });
};
